/**
 * Arranque del launcher (docs/FASE-7.md §7).
 *
 * El `AuthProvider` de `@continnua/auth-web` hace todo el trabajo de acceso: procesa el callback si
 * lo hay, intenta autenticación silenciosa si no, y deja al usuario `anonymous` si no había sesión.
 * `VITE_IDP_URL` es el **origen real** del IdP, no una ruta relativa: el flujo OIDC es una
 * navegación de primer nivel del navegador, no una llamada que pueda pasar por el proxy de Vite.
 */
import { AuthProvider } from "@continnua/auth-web/react";
import { SCOPES } from "@continnua/contracts";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import { App } from "./App";
import "./styles.css";

const idpUrl = (import.meta.env.VITE_IDP_URL as string | undefined) ?? "http://localhost:4100";
const clientId = (import.meta.env.VITE_LAUNCHER_CLIENT_ID as string | undefined) ?? "launcher-web";

/**
 * `platform:members:*` es lo que habilita la pantalla de usuarios (ADR-007): sin ellos el núcleo
 * rechaza la gestión de miembros aunque quien llame administre la organización.
 *
 * Ojo al dar de alta `launcher-web` en un despliegue real: `/oauth/authorize` responde
 * `invalid_scope` ante un scope que el cliente no tenga concedido, así que registrarlo sin estos
 * dos no deja la pantalla a medias —impide entrar al portal entero—.
 */
const SCOPE = [
  SCOPES.OPENID,
  SCOPES.PROFILE,
  SCOPES.EMAIL,
  SCOPES.OFFLINE_ACCESS,
  SCOPES.ORG_SWITCH,
  SCOPES.PLATFORM_MEMBERS_READ,
  SCOPES.PLATFORM_MEMBERS_WRITE,
].join(" ");

const root = document.getElementById("root");
if (!root) throw new Error("Falta el contenedor #root en index.html.");

createRoot(root).render(
  <StrictMode>
    <AuthProvider
      issuer={idpUrl}
      clientId={clientId}
      redirectUri={`${window.location.origin}/`}
      postLogoutRedirectUri={window.location.origin}
      scope={SCOPE}
    >
      <App />
    </AuthProvider>
  </StrictMode>,
);
